import {BCMSRecord ,BCMSRecordProperty} from "bcms-react"; 
import Classes, { Company, CompanyAccount } from "model";

/**
* @class ACHTemplate
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.achTemplates[0];
* const achTemplate = new ACHTemplate(record);
* @property {NUMBER} _id - The id of the template
* @property {STRING} _name - The name of the template
* @property {Company} _company - The company of the template.
* @property {BCMSDate} _createdDate - The created date of the template.
* @property {BCMSCurrency} _creditAmount - The credit amount of the template.
* @property {BCMSCurrency} _debitAmount - The debit amount of the template.
* @property {BCMSDate} _effectiveDate - The effective date of the template.
* @property {BCMSDate} _numberOfAccounts - The number of accounts associated with the template.
* @property {BCMSDate} _secCode - The sec code of the template.
* @property {String} _transactionType - The transaction type of the template
* @property {STRING} _companyDiscretionaryData - The companyDiscretionaryData of the template
* @property {BOOLEAN} _isRestricted - The isRestricted value of the template
* @property {STRING} _companyEntryDescription - The companyEntryDescription of the template
* @property {CompanyAccount} _companyAccount - The companyAccount of the template
*/
export default class ACHTemplate extends BCMSRecord {
    _id
    _name
    _company
    _createdDate
    _creditAmount
    _debitAmount
    _effectiveDate
    _numberOfAccounts
    _secCode
    _transactionType
    _companyDiscretionaryData
    _isRestricted
    _companyEntryDescription
    _companyAccount
    _denyAccessToUserIDs
    _offsetIndividually
    _achFromAccounts
    _achToParticipants

    constructor(record) {     
      super(record) 
    }
  }
  ACHTemplate.define({
    "recordType":"achTemplates",
    "recordIDProp":"id",
    "className" : "ACHTemplate",
    "recordProperties":[
      /** 
       * @name id
       * @instance
       * @property get {NUMBER} - gets the id of the template
       * @property set {NUMBER} - sets the id of the template
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToString("id","_id"),
      /** 
       * @name name
       * @instance
       * @property get {STRING} - gets the name of the template
       * @property set {STRING} - sets the name of the template
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToString("name","_name"),

      /** 
       * @name company
       * @instance
       * @property get {CompanyAccount} - gets the company
       * @property set {CompanyAccount} - sets the company
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToClass("company","_company", () => Company),

      /** 
       * @name createdDate
       * @instance
       * @property get {CompanyAccount} - gets the createdDate date
       * @property set {CompanyAccount} - sets the createdDate date
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToDate("createdDate","_createdDate"),

      /** 
       * @name creditAmount
       * @instance
       * @property get {CompanyAccount} - gets the creditAmount
       * @property set {CompanyAccount} - sets the creditAmount
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToCurrency("creditAmount","_creditAmount"),

      /** 
       * @name debitAmount
       * @instance
       * @property get {CompanyAccount} - gets the debitAmount
       * @property set {CompanyAccount} - sets the debitAmount
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToCurrency("debitAmount","_debitAmount"),

      /** 
       * @name effectiveDate
       * @instance
       * @property get {CompanyAccount} - gets the effectiveDate
       * @property set {CompanyAccount} - sets the effectiveDate
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToDate("effectiveDate","_effectiveDate"),

      /** 
       * @name numberOfAccounts
       * @instance
       * @property get {CompanyAccount} - gets the numberOfAccounts
       * @property set {CompanyAccount} - sets the numberOfAccounts
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToString("numberOfAccounts","_numberOfAccounts"),

      /** 
       * @name secCode
       * @instance
       * @property get {STRING} - gets the secCode
       * @property set {STRING} - sets the secCode
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToString("secCode","_secCode"),

      /** 
       * @name transactionType
       * @instance
       * @property get {STRING} - gets the transactionType of the template
       * @property set {STRING} - sets the transactionType of the template
       * @memberof ACHTemplate
       */
       BCMSRecordProperty.mapToString("transactionType","_transactionType"),
      /** 
       * @name companyDiscretionaryData
       * @instance
       * @property get {STRING} - gets the companyDiscretionaryData of the template
       * @property set {STRING} - sets the companyDiscretionaryData of the template
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToString("companyDiscretionaryData","_companyDiscretionaryData"),
      /** 
       * @name isRestricted
       * @instance
       * @property get {STRING} - gets the isRestricted of the template
       * @property set {STRING} - sets the isRestricted of the template
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToBoolean("isRestricted","_isRestricted"),

      /** 
       * @name offsetIndividually
       * @instance
       * @property get {STRING} - gets the offsetIndividually of the template
       * @property set {STRING} - sets the offsetIndividually of the template
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToBoolean("offsetIndividually","_offsetIndividually"),

      /** 
       * @name companyEntryDescription
       * @instance
       * @property get {STRING} - gets the companyEntryDescription of the template
       * @property set {STRING} - sets the companyEntryDescription of the template
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToString("companyEntryDescription","_companyEntryDescription"),
      /** 
       * @name companyAccount
       * @instance
       * @property get {CompanyAccount} - gets the companyAccount of the template
       * @property set {CompanyAccount} - sets the companyAccount of the template
       * @memberof ACHTemplate
       */
      BCMSRecordProperty.mapToClass("companyAccount","_companyAccount", () => CompanyAccount),

      /**
       * @name _denyAccessToUserIDs
       * @instance
       * @property get {ARRAY} - gets the _denyAccessToACHTemplateIDs
       * @property set {ARRAY} - sets the _denyAccessToACHTemplateIDs
       * @note The mapToArray currently does not work correctly when the toRecord function is called
       * @memberof ACHTemplate
       */
          BCMSRecordProperty.mapTo({
            id: "denyAccessToUserIDs",
            instanceField: "_denyAccessToUserIDs",
            setter: function(value) {
              if(value === undefined || Array.isArray(value)) {
                this._denyAccessToUserIDs = value;
              } else {
                this._denyAccessToUserIDs = value === "" ? [] : value.split(",");
              }
            },
            toRecord: function() {
                return this._denyAccessToUserIDs === undefined || this._denyAccessToUserIDs.length === 0
                    ? ""
                    : this._denyAccessToUserIDs.reduce((result, item) => `${ result },${ item }`);
            }
        }),

        /**
         * @name achFromAccounts
         * @instance
         * @property get {ARRAY} - gets the _denyAccessToACHTemplateIDs
         * @property set {ARRAY} - sets the _denyAccessToACHTemplateIDs
         * @note The mapToArray currently does not work correctly when the toRecord function is called
         * @memberof ACHTemplate
         */
        BCMSRecordProperty.mapTo({
            id: "achFromAccounts",
            instanceField: "_achFromAccounts",
            setter: function(value) {
              if(value === undefined || Array.isArray(value)) {
                this._achFromAccounts = value;
              } else {
                this._achFromAccounts = value === "" ? [] : value.split(",");
              }
            },
            toRecord: function() {
                return this._achFromAccounts === undefined || this._achFromAccounts.length === 0
                    ? ""
                    : this._achFromAccounts.reduce((result, item) => `${ result },${ item }`);
            }
        }),

        /**
         * @name achToParticipants
         * @instance
         * @property get {ARRAY} - gets the _denyAccessToACHTemplateIDs
         * @property set {ARRAY} - sets the _denyAccessToACHTemplateIDs
         * @note The mapToArray currently does not work correctly when the toRecord function is called
         * @memberof ACHTemplate
         */
        BCMSRecordProperty.mapTo({
            id: "achToParticipants",
            instanceField: "_achToParticipants",
            setter: function(value) {
              if(value === undefined || Array.isArray(value)) {
                this._achToParticipants = value;
              } else {
                this._achToParticipants = value === "" ? [] : value.split(",");
              }
            },
            toRecord: function() {
                return this._achToParticipants === undefined || this._achToParticipants.length === 0
                    ? ""
                    : this._achToParticipants.reduce((result, item) => `${ result },${ item }`);
            }
        })
    ]  
})




